import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { MAX_SEARCH_DOMAINS, normalizeSearchDomain, splitDomainInput } from '../utils/searchDomains';

interface SearchSourcesPickerProps {
  domains: string[];
  onChange: (domains: string[]) => void;
}

const FONT = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const ACCENT = '#0f766e';
const ACCENT_BG = '#ccfbf1';
const ACCENT_BORDER = '#14b8a6';

const LinkIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
  </svg>
);

const SearchSourcesPicker: React.FC<SearchSourcesPickerProps> = ({ domains, onChange }) => {
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isRestricted = domains.length > 0;

  useEffect(() => {
    if (!isOpen) return;

    inputRef.current?.focus();
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const addDraft = () => {
    const entries = splitDomainInput(draft);

    if (entries.length === 0) return;

    const next = [...domains];
    const invalid: string[] = [];
    for (const entry of entries) {
      const domain = normalizeSearchDomain(entry);
      if (!domain) {
        invalid.push(entry);
      } else if (!next.includes(domain)) {
        next.push(domain);
      }
    }

    if (next.length > MAX_SEARCH_DOMAINS) {
      setError(`You can add up to ${MAX_SEARCH_DOMAINS} websites.`);
      return;
    }

    onChange(next);
    // Keep the bad entries in the box so the user can fix them rather than retype.
    setDraft(invalid.join(', '));
    setError(invalid.length ? `Not a website address: ${invalid.join(', ')}` : null);
  };

  const removeDomain = (domain: string) => {
    onChange(domains.filter((d) => d !== domain));
  };

  const label = !isRestricted
    ? 'All websites'
    : domains.length === 1
      ? domains[0]
      : `${domains.length} websites`;

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        title={isRestricted ? `Searching only: ${domains.join(', ')}` : 'Choose which websites to search'}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          borderRadius: '16px',
          backgroundColor: isRestricted ? ACCENT_BG : colors.surface,
          border: `1px solid ${isRestricted ? ACCENT_BORDER : colors.border}`,
          fontSize: '14px',
          color: isRestricted ? ACCENT : colors.textSecondary,
          fontFamily: FONT,
          cursor: 'pointer',
          maxWidth: '200px',
          whiteSpace: 'nowrap',
        }}
      >
        <LinkIcon />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      </button>

      {isOpen && (
        <div
          onKeyDown={(e) => {
            if (e.key === 'Escape') setIsOpen(false);
          }}
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 12px)',
            left: 0,
            width: '340px',
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            padding: '14px',
            zIndex: 1000,
            fontFamily: FONT,
            textAlign: 'left',
          }}
        >
          <div style={{ fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
            Search only these websites
          </div>
          <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '2px', marginBottom: '10px' }}>
            Leave empty to search the whole web. Subdomains are included.
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                // Enter would otherwise submit the chat form this picker sits in.
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addDraft();
                }
              }}
              placeholder="e.g. reuters.com, https://www.sec.gov"
              style={{
                flex: 1,
                padding: '8px 10px',
                borderRadius: '8px',
                border: `1px solid ${error ? '#ef4444' : colors.border}`,
                backgroundColor: colors.background,
                color: colors.textPrimary,
                fontSize: '13px',
                fontFamily: FONT,
                outline: 'none',
              }}
            />
            <button
              type="button"
              onClick={addDraft}
              disabled={!draft.trim()}
              style={{
                padding: '8px 14px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: draft.trim() ? ACCENT : colors.border,
                color: 'white',
                fontSize: '13px',
                fontFamily: FONT,
                cursor: draft.trim() ? 'pointer' : 'not-allowed',
              }}
            >
              Add
            </button>
          </div>

          {error && (
            <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '6px' }}>{error}</div>
          )}

          {isRestricted && (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '12px' }}>
                {domains.map((domain) => (
                  <span
                    key={domain}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 6px 4px 10px',
                      borderRadius: '12px',
                      backgroundColor: ACCENT_BG,
                      color: ACCENT,
                      fontSize: '12px',
                      maxWidth: '100%',
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{domain}</span>
                    <button
                      type="button"
                      onClick={() => removeDomain(domain)}
                      aria-label={`Remove ${domain}`}
                      style={{
                        display: 'flex',
                        border: 'none',
                        background: 'transparent',
                        color: ACCENT,
                        cursor: 'pointer',
                        padding: '2px',
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
                <span style={{ fontSize: '11px', color: colors.textMuted }}>
                  {domains.length} of {MAX_SEARCH_DOMAINS}
                </span>
                <button
                  type="button"
                  onClick={() => onChange([])}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: colors.textSecondary,
                    fontSize: '12px',
                    fontFamily: FONT,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Clear all
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchSourcesPicker;
